import _ from 'lodash';
import moment from 'moment';

import { bindCallbacks, statusText } from 'scripts/infrastructure/backends/fake_backend_http/lib/common';
import { ConversationItemPublisher } from 'scripts/infrastructure/backends/fake_backend/conversation_items_service';
import htmlToText from 'models/conversation_item/html_to_text';
import IdGenerator from 'scripts/domain/contracts/id_generator';

// Fakes the task side-thread routes: starting a thread creates a task in the customer's latest
// conversation and records the email as the task's first child; replies append children. Children
// are broadcast like the real backend does (with parentItemId) so the desktop refetches the thread.
export default class TaskThreadService {
  constructor(database, pubsub) {
    this.getDatabase = database;
    this.publisher = new ConversationItemPublisher(pubsub);
  }

  start(attrs, callback, path, { orgId }) {
    const db = this.getDatabase(orgId);
    const customerId = attrs.customerId;
    const customer = _.find(db.customers, { id: customerId });
    if (!customer) {
      callback(null, { status: 404, statusText: statusText(404) });
      return;
    }

    const agent = db.currentAgent || {};
    const timestamp = new Date().toISOString();
    const subject = _.get(attrs, 'email.subject', '');
    const task = {
      id: IdGenerator.newId(),
      customerId,
      conversationId: _.get(_.last(customer.conversations), 'id'),
      initiator: { type: 'AGENT', id: agent.id },
      timestamp,
      version: 0,
      content: {
        type: 'TASK',
        note: subject,
        bodyHtml: subject,
        assignee: attrs.assignee,
        dueAt: attrs.dueAt || moment().add(1, 'day').toISOString(),
        hasNotifiedDue: false,
        topicIds: [],
        customAttributes: [],
        updatedAt: timestamp,
      },
    };
    customer.conversationHistory = customer.conversationHistory || [];
    customer.conversationHistory.push(task);
    this.publisher.publishConversationItem(IdGenerator.newId(), orgId, customerId, task);

    const child = this._addChild(orgId, customer, task, attrs.email, agent);
    callback(null, {
      status: 200,
      statusText: statusText(200),
      response: { taskItemId: task.id, itemId: child.id, customerId, task },
    });
  }

  reply(attrs, callback, path, { orgId, taskItemId }) {
    const { customer, task } = this._findTask(orgId, taskItemId);
    if (!task) {
      callback(null, { status: 404, statusText: statusText(404) });
      return;
    }

    const child = this._addChild(orgId, customer, task, attrs.email, this.getDatabase(orgId).currentAgent || {});
    callback(null, { status: 200, statusText: statusText(200), response: child });
  }

  fetchThread(attrs, callback, path, { orgId, taskItemId }) {
    const items = this._threads(orgId)[taskItemId] || [];
    const first = items[0];
    callback(null, {
      status: 200,
      statusText: statusText(200),
      response: {
        channel: first ? 'EMAIL' : undefined,
        counterpart: first ? { address: first.email.to[0] } : undefined,
        items,
      },
    });
  }

  _addChild(orgId, customer, task, email, agent) {
    const addresses = list => _.map(list || [], a => a.email);
    const bodyHtml = _.get(email, 'body.html', '');
    const item = {
      id: IdGenerator.newId(),
      direction: 'OUTBOUND',
      agentId: agent.id,
      timestamp: new Date().toISOString(),
      email: {
        from: email.from,
        to: addresses(email.to),
        cc: addresses(email.cc),
        bcc: addresses(email.bcc),
        subject: email.subject,
        bodyHtml,
        bodyPlain: _.get(email, 'body.text') || htmlToText(bodyHtml),
        attachments: email.attachments || [],
      },
    };

    const threads = this._threads(orgId);
    threads[task.id] = (threads[task.id] || []).concat(item);

    // The real backend broadcasts the stored child; the desktop keeps it out of the feed but uses it
    // as the cue to refetch the thread.
    this.publisher.publishConversationItem(IdGenerator.newId(), orgId, customer.id, {
      id: item.id,
      customerId: customer.id,
      parentItemId: task.id,
      initiator: { type: 'AGENT', id: agent.id },
      timestamp: item.timestamp,
      version: 0,
      content: { type: 'EMAIL', ...item.email },
    });

    return item;
  }

  _findTask(orgId, taskItemId) {
    const db = this.getDatabase(orgId);
    for (const customer of db.customers || []) {
      const task = _.find(customer.conversationHistory, { id: taskItemId });
      if (task) return { customer, task };
    }
    return {};
  }

  _threads(orgId) {
    const db = this.getDatabase(orgId);
    db.taskThreads = db.taskThreads || {};
    return db.taskThreads;
  }

  getRoutes() {
    return bindCallbacks(
      {
        '/api/v1/orgs/:orgId/task-threads': {
          POST: this.start,
        },
        '/api/v1/orgs/:orgId/task-threads/:taskItemId': {
          GET: this.fetchThread,
          POST: this.reply,
        },
      },
      this
    );
  }
}
